import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames';
import { AgGridReact } from '@ag-grid-community/react';
import { AllModules } from '@ag-grid-enterprise/all-modules';
import toastr from '../toastr';
import { modal } from '../modal/ModalEmitter';
import browserHistory from '../../history';
import getGridOptions from './gridOptions';
import AuthService from '../../services/AuthService';
import ApiService from '../../services/ApiService';
import FileService from '../../services/FileService';
import ListViewToolbar from './ListViewToolbar';
import ListRowToolbar from './ListRowToolbar';
import SaveGridLayoutForm from '../grid/SaveGridLayoutForm';
import LoadGridLayoutForm from '../grid/LoadGridLayoutForm';
import FilterBar from '../archive/FilterBar';
import { replaceBars } from '../../util/general';
import { legacyGetSortModel } from '../../util/grid';
import { editFormDialog } from '../modal/ModalEmitter';
import '../../styles/listview.scss';

const sizePerButton = 32;

const populateVariables = (config, rowData) => {
  const newConfig = {};

  Object.keys(config).forEach(key => {
    if (/^\$__/.test(config[key])) {
      const lookupVar = config[key].replace('$__', '');

      if (lookupVar === 'lookupValue' || lookupVar === 'itemid') {
        newConfig[key] = rowData.itemid;
        return;
      }

      if (rowData[lookupVar] !== undefined) {
        newConfig[key] = rowData[lookupVar] === null ? '' : rowData[lookupVar];
        return;
      }
    }
    newConfig[key] = config[key];
  });

  return newConfig;
};

class ListView extends Component {
  gridOptions = {
    ...getGridOptions(),
    getRowId: item => {
      const { idFieldName } = this.props;
      const rowData = item.data;

      if (Array.isArray(idFieldName)) {
        return idFieldName.map(n => rowData[n]).join('-');
      }

      return rowData[idFieldName] || rowData.itemid || rowData.id || (Math.random() + 1).toString(36); // last one is random to avoid ag grid errors
    },
  };

  constructor(props) {
    super(props);

    this.customWidthsSet = false;

    this.listViewToolbar = React.createRef();

    this.state = {
      key: 1,
      terms: props.initialQuery,
      verticalHeaders: props.verticalHeaders,
      resultCount: 0,
      theme: localStorage.getItem('list.theme') || 'compact',
      layout: props.layout || null,
      hasFilter: false,
    };

    this.beforePrint = null;
    this.afterPrint = null;

    this.searchOptions = {};
  }

  componentDidMount() {
    if (this.state.terms && this.props.serverSide) {
      this.debounceHandleServerSearch(this.state.terms);
    }

    if (this.props.refreshTime) {
      this.refreshInterval = setInterval(() => {
        if (document.visibilityState === 'hidden') {
          return;
        }

        if (this.agGrid) {
          if (this.props.serverSide) {
            this.agGrid.api.refreshInfiniteCache();
          } else {
            this.props.handleRefresh({ skipLog: true });
          }
        }
      }, this.props.refreshTime * 1000);
    }

    this.beforePrint = () => {
      const agGrid = this.agGrid;

      if (!agGrid) {
        return false;
      }

      if (modal.isOpen) {
        return false;
      }

      if (this.props.rowData.length > 500) {
        window.confirm('List too big! It will not print correctly. Export CSV instead.'); // eslint-disable-line
        return false;
      }

      if (agGrid.api) {
        agGrid.api.setDomLayout('print');
      }

      return true;
    };

    this.afterPrint = () => {
      const agGrid = this.agGrid;

      if (!agGrid) {
        return;
      }

      if (agGrid && agGrid.api) {
        agGrid.api.setDomLayout(null);
      }
    };

    window.addEventListener('focus', this.sizeGrid);
    window.addEventListener('beforeprint', this.beforePrint);
    window.addEventListener('afterprint', this.afterPrint);
  }

  componentWillUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }

    window.removeEventListener('focus', this.sizeGrid);
    window.removeEventListener('beforeprint', this.beforePrint);
    window.removeEventListener('afterprint', this.afterPrint);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.serverSide !== this.props.serverSide) {
      this.setState({ key: this.state.key + 1 });
    }
  }

  onGridReady = () => {
    this.refreshCount();
    this.sizeGrid();

    // Through trial and error, I discovered loading the layout in the next paint cycle resolves layout application issues.
    setTimeout(() => this.loadLayout(), 0);
  };

  onModelUpdated = () => {
    this.sizeGrid();
    this.refreshCount();
  };

  onFilterChanged = () => {
    this.refreshCount();

    if (this.gridApiReady()) {
      this.setState({ hasFilter: !_.isEmpty(this.agGrid.api.getFilterModel()) });
    }
  };

  onViewportChanged = () => {};

  getDataSource = () => {
    return {
      rowCount: null,
      getRows: params => {
        this.searchOptions = {
          ...this.searchOptions,
          from: params.startRow,
          limit: params.endRow - params.startRow,
          agSort: btoa(JSON.stringify(params.sortModel)),
          agFilter: btoa(JSON.stringify(params.filterModel)),
        };

        this.props.getData(this.searchOptions).then(res => {
          const { list, resultCount } = res.data;
          this.setState({ resultCount });

          if (!list || !list.length) {
            params.successCallback([], 0);
            return;
          }

          const lastRow = list.length >= 1000 ? -1 : resultCount;
          params.successCallback(list, lastRow);
        });
      },
    };
  };

  getServerSideOptions = () => {
    if (!this.props.serverSide) {
      return {};
    }

    return {
      datasource: this.getDataSource(),
      rowModelType: 'infinite',
      cacheBlockSize: 1000,
      maxBlocksInCache: 10,
    };
  };

  getCount() {
    if (this.props.serverSide) {
      return this.state.resultCount;
    }

    if (!this.gridApiReady() || !this.agGrid.api.getModel().rowsToDisplay) {
      return 0;
    }

    return this.agGrid.api.getModel().rowsToDisplay.length;
  }

  getMainMenuItems = params => {
    const menuItems = [];

    params.defaultItems.forEach(item => {
      // disable grouping with server side lists
      if (this.props.serverSide && ['rowGroup', 'rowUnGroup'].includes(item)) {
        return;
      }

      menuItems.push(item);
    });

    return menuItems;
  };

  getContextMenuItems = (params = {}) => {
    const helpers = _.get(params, 'column.colDef.helpers') || [];
    const fieldName = _.get(params, 'column.colDef.field');
    const menu = [];
    const modalConfig = modal.config;
    const defaultIcon = modal.defaultIcon;

    if (params.value !== undefined && params.value !== null && params.value !== '') {
      const name = _.get(params, 'node.data.Name');
      const suffix = name ? ` - ${name}` : '';

      helpers.forEach(helper => {
        const helperCfg = modalConfig[helper];
        if (helper && (helper.name || helper === 'listEditItem' || helper === 'dbEditItem')) {
          const icon = helper.icon
            ? `<i class="fad fa-${helper.icon} fa-fw grid-icon" />`
            : '<i class="fad fa-edit fa-fw grid-icon" />';

          const name = helper.label ? helper.label : `Edit ${params.value}`;
          const query = helper.query ? populateVariables(helper.query, params.node.data) : {};
          const lookupKey = query.itemid || params.value;
          const title =
            helper.title || helper.label ? `${helper.title || helper.label} ${lookupKey}` : `Editing ${lookupKey}`;
          const render = helper.name || helper;
          const itemName = helper.itemName || this.props.itemName;
          if (AuthService.hasPermission(itemName)) {
            menu.push({
              icon,
              name,
              action: () => {
                modal.open(render, {
                  ...editFormDialog,
                  itemName,
                  lookupKey,
                  query,
                  render,
                  title,
                });
              },
            });
          }
        }

        // No config freeform (SMM1)
        // TODO expand into helperCfg section
        if (!helperCfg && helper.redirect) {
          const icon = helper.icon
            ? `<i class="fad fa-${helper.icon} fa-fw grid-icon" />`
            : '<i class="fad fa-edit fa-fw grid-icon" />';

          const lookup = _.get(params, 'node.data');

          menu.push({
            icon,
            name: helper.title || 'More Info',
            action: () => {
              const url = replaceBars(helper.redirect, { lookupValue: params.value, ...lookup });
              window.open(url, helper.target || '_blank');
            },
          });
          return;
        }

        if (helperCfg) {
          const icon = helperCfg.icon
            ? `<i class="${helperCfg.icon} fa-fw grid-icon" />`
            : `<i class="${defaultIcon} fa-fw grid-icon" />`;

          menu.push({
            icon,
            name: helperCfg.title || 'More Info',
            action: () => {
              if (helperCfg.redirect) {
                let qs = '';
                if (helperCfg.query) {
                  Object.keys(helperCfg.query).forEach(k => {
                    const value = helperCfg.query[k];
                    const listData = _.get(params, `node.data.${value}`);
                    if (listData) {
                      qs = qs ? `${qs}&${k}=${listData}` : `?${k}=${listData}`;
                    }
                  });
                }

                if (helperCfg.target) {
                  window.open(`${helperCfg.redirect}${qs}`, helperCfg.target);
                } else {
                  browserHistory.push(`${helperCfg.redirect}${qs}`);
                }
              } else {
                const options = {
                  ...helperCfg,
                  lookupKey: params.value,
                  title: `${helperCfg.title}${suffix}`,
                  contextName: fieldName,
                };
                modal.open(helper, options);
              }
            },
          });
        }
      });
    }

    if (menu.length) {
      menu.push('separator');
    }

    menu.push('copy', 'copyWithHeaders');
    return menu;
  };

  // TODO: This is mutating columnDefs
  getColumnDefs = () => {
    const {
      attachments,
      columnDefs,
      showRowToolbar,
      canEdit,
      canDelete,
      canUpload,
      canRead,
      canCopy,
      canViewAttachments,
      canNotes,
      idFieldName,
      itemName,
      rowButtonCount,
    } = this.props;

    if (!columnDefs.length) {
      return [];
    }

    if (showRowToolbar) {
      // Dynamically size column with toolbar, initially set padding
      let width = 10;

      if (rowButtonCount) {
        width += sizePerButton * rowButtonCount;
      } else {
        // Increase width for every button that'll be available to user
        [canEdit, canDelete, canRead, canCopy, canNotes, canViewAttachments || canUpload].forEach(op => {
          if (op) {
            width += sizePerButton;
          }
        });
      }

      const toolbarColumn = {
        colId: 'toolbar',
        width,
        pinned: 'left',
        headerName: '',
        field: 'action',
        sortable: false,
        suppressSizeToFit: true,
        suppressMenu: true,
        enableRowGroup: false,
        cellStyle: { textAlign: 'center' },
        cellRenderer: cellProps => {
          /*
            The ListRowToolbar needs the id in order to perform ops on
            it's relative data.  The id can live on itemid, id, or an
            explicitly defined key on idFieldName.
           */

          if (!cellProps.data) {
            return null;
          }

          let itemId = cellProps.data.itemid || String(cellProps.data.id);
          if (idFieldName) {
            itemId = String(cellProps.data[idFieldName]);
          }

          const attachmentCount = attachments[itemId] ? attachments[itemId].attachments.length : 0;

          return (
            <ListRowToolbar
              key={itemId}
              attachments={attachmentCount}
              canAdd={this.props.canAdd}
              canCopy={this.props.canCopy}
              canDelete={this.props.canDelete}
              canEdit={this.props.canEdit}
              canNotes={this.props.canNotes}
              canRead={this.props.canRead}
              canUpload={this.props.canUpload}
              canViewAttachments={this.props.canViewAttachments}
              handleRemoveItem={this.props.handleRemoveItem}
              itemId={itemId}
              itemName={this.props.itemName}
              namespace={this.props.namespace}
              rowData={cellProps.data}
            />
          );
        },
      };

      if (columnDefs[0].field === 'action') {
        columnDefs[0] = toolbarColumn;
      } else {
        columnDefs.unshift(toolbarColumn);
      }
    }

    const defaultFilter = 'agMultiColumnFilter';
    const defaultFilterParams = {
      filters: [
        {
          filter: 'agTextColumnFilter',
          filterParams: {
            buttons: ['clear'],
          },
        },
        {
          filter: 'agSetColumnFilter',
          filterParams: {
            buttons: ['reset'],
            values: this.props.serverSide
              ? params => {
                  ApiService.get(`/list/${itemName}/unique`, { params: { field: params.colDef.field } }).then(
                    response => {
                      if (!response.data || !response.data.length) {
                        params.success([]);
                      }

                      params.success(response.data.map(x => x.key));
                    }
                  );
                }
              : undefined,
          },
        },
      ],
    };

    columnDefs.forEach(column => {
      column.colId = column.colId || column.field;
      if (column.width && !column.pinned) {
        this.customWidthsSet = true;
      }

      column.filter = column.filter || defaultFilter;
      column.filterParams = column.filterParams || column.filter === defaultFilter ? defaultFilterParams : undefined;
      column.enableRowGroup = true;

      if (column.helpers && column.helpers.length) {
        column.cellRenderer = 'basicCellRenderer';
      }

      if (column.editable) {
        column.cellRenderer = 'basicCellRenderer';
      }
    });

    const keyById = _.keyBy(columnDefs, 'colId');

    // hack that ensures column order is maintained on rerender (ie quick search)
    // included hide in the hack!
    const orderedColDefs = [];
    if (this.agGrid && this.agGrid.columnApi) {
      const c = this.agGrid.columnApi.getColumnState();
      for (const col of c) {
        if (keyById[col.colId]) {
          keyById[col.colId].hide = col.hide;
          keyById[col.colId].width = col.width;
          orderedColDefs.push(keyById[col.colId]);
        }
      }

      return orderedColDefs;
    }

    return columnDefs;
  };

  refreshCount() {
    // refactor this out
    if (this.listViewToolbar.current) {
      this.listViewToolbar.current.setCount(this.getCount());
    }
  }

  sizeGrid = () => {
    if (!this.agGrid) {
      return;
    }

    // No-worky v26+
    // this.agGrid.eGridDiv.style.width = '100%';
    // this.agGrid.eGridDiv.style.height = 'initial';

    // v26+
    const eGridDiv = document.querySelector('#grid-wrapper > div');
    eGridDiv.style.setProperty('width', '100%');
    eGridDiv.style.setProperty('height', 'initial');
    //

    if (this.customWidthsSet) {
      // Don't wipe out any width values defined in columnDefs
      return;
    }

    setTimeout(() => {
      if (this.agGrid && this.agGrid.columnApi) {
        this.agGrid.columnApi.autoSizeAllColumns();
      }
    }, 200); // hack to allow images to render before col calcs

    // use this if anyone cares to expand on unused grid space
    // this.agGrid.api.sizeColumnsToFit();
  };

  loadLayout() {
    const { layout } = this.state;

    if (!this.props.itemName) {
      return;
    }

    if (!layout) {
      this.resetGrid();
      return;
    }

    if (layout && _.isObject(layout) && this.agGrid) {
      const { c, f, s } = layout.layout;

      // Don't use saved widths for the toolbar column
      if (c[0].colId === 'action') {
        delete c[0].width;
      }

      const columnDefs = this.getColumnDefs();

      if (f) {
        for (const field of Object.keys(f)) {
          const filterType = f[field].filterType;
          if (['set', 'text'].includes(filterType)) {
            const columnDef = _.find(columnDefs, o => o.field === field);

            // We're only supporting set filters on string columns from now on.
            // It's possible, but probably unlikely, someone saved a set filter on a non-string column.
            if (columnDef && columnDef.valueType !== 'string') {
              delete f[field];
            } else {
              // Transpose the set/text filter to the multifilter model we use in v26+.
              f[field] = {
                filterType: 'multi',
                filterModels: [filterType === 'text' ? f[field] : null, filterType === 'set' ? f[field] : null],
              };
            }
          }
        }
      }

      this.agGrid.api.setFilterModel(f);
      this.agGrid.api.onFilterChanged(); // Don't think we need this anymore

      // It seems any of the filter methods above bring old column state back, so this needs to be executed last
      setTimeout(() => {
        if (this.agGrid && this.agGrid.columnApi) {
          this.agGrid.columnApi.applyColumnState({
            state: c,
            applyOrder: true,
          });
        }
      }, 10);

      // Old layouts saved pre v26 may have saved sort separately.  Apply partial state diff here.
      if (s && s.length) {
        this.agGrid.columnApi.applyColumnState({ state: s });
      }
    }
  }

  hasSort = () => {
    if (this.agGrid.columnApi) {
      const columnState = this.agGrid.columnApi.getColumnState();
      for (const col of columnState) {
        if (col.sort) {
          return true;
        }
      }
    }

    return false;
  };

  postSortRows = params => {
    const rowNodes = params.nodes;
    const columnApi = params.columnApi;
    const { serverSide } = this.props;
    const { terms } = this.state;

    // return for no filter text or sort already applied
    if (!terms || serverSide || !this.agGrid || this.hasSort()) {
      return;
    }

    if (columnApi.columnModel && columnApi.columnModel.rowGroupColumns.length !== 0) {
      return;
    }

    // puts exact itemid matches on top
    rowNodes.sort((a, b) => {
      const aVal = a.data.itemid;
      const bVal = b.data.itemid;

      if (aVal && aVal === terms) {
        return -1;
      }

      if (bVal && bVal === terms) {
        return 1;
      }

      return 0;
    });
  };

  handleRefresh = () => {
    if (!this.agGrid) {
      return;
    }

    if (this.props.serverSide) {
      return this.props.handleRefresh({ skipCache: true }).then(() => {
        if (this.gridApiReady()) {
          this.agGrid.api.refreshInfiniteCache();
        }
      });
    }

    return this.props.handleRefresh({ skipCache: true });
  };

  handleLoadLayout = () => {
    modal.open({
      title: 'Load Layout',
      message: (
        <LoadGridLayoutForm
          currentLayoutId={_.get(this.state.layout, 'id')}
          pageName={this.props.itemName}
          optionsHash={null}
          onCancel={() => modal.close()}
          onSubmit={layout => {
            this.setState({ layout }, () => this.loadLayout());
            modal.close();
          }}
        />
      ),
      style: 'center',
      size: 'md',
      hidePrint: true,
      hideFooter: true,
    });
  };

  handleSaveLayout = () => {
    const { itemName } = this.props;

    if (!itemName) {
      toastr.error('Save Failed', 'Layout cannot be saved for this list');
      return;
    }

    const values = {
      pageName: itemName,
    };

    if (this.state.layout) {
      const { id, name, description, isPublic } = this.state.layout;
      values.id = id;
      values.name = name;
      values.description = description;
      values.isPublic = isPublic;
    }

    values.layout = {
      c: this.agGrid.columnApi.getColumnState(), // includes sort model v26+
      f: this.agGrid.api.getFilterModel(),
    };

    modal.open({
      title: 'Save Layout',
      message: (
        <SaveGridLayoutForm
          values={values}
          onCancel={() => modal.close()}
          onSubmit={layout => {
            this.setState({ layout });
            modal.close();
            toastr.success('Success!', 'Layout Saved');
          }}
        />
      ),
      style: 'center',
      size: 'md',
      hidePrint: true,
      hideFooter: true,
    });
  };

  handleRemoveLayout = () => {
    const { layout } = this.state;

    if (!layout) {
      return;
    }

    modal.confirm({
      title: 'Delete Layout Confirmation',
      message: 'This will delete your layout. Are you sure?',
      confirmLabels: ['Delete', 'Cancel'],
      callback: () => {
        ApiService.delete(`grid-layouts/${layout.id}`)
          .then(() => {
            this.resetGrid();
            toastr.success('Layout Removed');
            this.setState({ layout: null });
          })
          .catch(() => {
            toastr.error('Error!', 'Network error.  Please try again later.');
          });
      },
    });
  };

  updateQueryString = value => {
    if (value) {
      browserHistory.push({ search: `?q=${value}` });
    } else {
      browserHistory.push({ search: '' });
    }
  };

  handleSearch = value => {
    if (this.props.serverSide) {
      this.debounceHandleServerSearch(value);
      return;
    }

    this.debounceHandleQuickSearch(value);
  };

  debounceHandleQuickSearch = _.debounce(value => {
    this.updateQueryString(value);
    this.setState({ terms: value });
  }, 500);

  debounceHandleServerSearch = _.debounce(value => {
    this.updateQueryString(value);
    this.searchOptions.q = value || '';

    if (this.gridApiReady()) {
      this.agGrid.api.setDatasource(this.getDataSource());
    }
    this.setState({ terms: value }, () => this.props.search({ q: this.state.terms }));
  }, 500);

  handleExport = () => {
    const { count, itemName } = this.props;

    const fileName = `${this.props.itemName}-${moment().format('YYYY-MM-DD')}.csv`;

    if (!count || count <= 10000) {
      const columnKeys = this.props.columnDefs.map(columnDef => columnDef.field).filter(key => key !== 'action');
      this.agGrid.api.exportDataAsCsv({ columnKeys, fileName });
      return;
    }

    // ag grid has partial list loaded for large lists, call backend for export
    if (this.props.namespace === 'list') {
      const agSort = btoa(JSON.stringify(legacyGetSortModel(this.agGrid.columnApi.getColumnState())));
      const agFilter = btoa(JSON.stringify(this.agGrid.api.getFilterModel()));

      ApiService.get(`/list/${itemName}`, {
        responseType: 'blob',
        params: { export: true, agSort, agFilter, q: this.state.terms },
      }).then(response => {
        FileService.saveAs(response.data, fileName);
      });
    }
  };

  handleClearFilters = () => {
    this.agGrid.api.setFilterModel(null);
    this.handleSearch('');
  };

  handleAddNew = () => {
    if (this.props.itemName === 'EQS1' || this.props.itemName === 'EQS2') {
      modal.open('quickCreate', {
        submitCallback: () => modal.close(),
      });
      return;
    }

    browserHistory.push(`/${this.props.namespace}/${this.props.itemName}/new`);
  };

  handleToggleVerticalHeaders = () => {
    this.setState(prevState => {
      if (this.gridApiReady()) {
        if (prevState.verticalHeaders) {
          this.agGrid.api.setHeaderHeight(32);
        } else {
          this.agGrid.api.setHeaderHeight(150);
        }
      }

      return { verticalHeaders: !prevState.verticalHeaders };
    });
  };

  handleThemeChange = theme => {
    localStorage.setItem('list.theme', theme);
    this.setState({ theme, key: this.state.key + 1 });
  };

  resetGrid = () => {
    if (this.agGrid && this.agGrid.columnApi) {
      this.agGrid.columnApi.resetColumnState();
    }

    if (this.gridApiReady()) {
      this.agGrid.api.setFilterModel(null);
      this.agGrid.api.onFilterChanged();
    }
  };

  findRowById(id) {
    const { idFieldName } = this.props;

    if (!this.gridApiReady()) {
      return null;
    }

    let foundNode = null;

    this.agGrid.api.forEachNode(node => {
      if (_.get(node, ['data', idFieldName]) === id) {
        foundNode = node;
      }
    });

    return foundNode;
  }

  addRow(data) {
    if (!this.agGrid) {
      return;
    }

    if (this.props.serverSide) {
      // TODO check with new ag grid
      // without this, row toolbar is bugged, should be fixed in new ag grid
      this.setState({ key: this.state.key + 1 });
    } else {
      if (this.gridApiReady()) {
        this.agGrid.api.updateRowData({ add: [data] });
      }
    }
  }

  removeRowById(id) {
    if (this.props.serverSide) {
      // TODO check with new ag grid
      this.setState({ key: this.state.key + 1 });
      return;
    }

    const node = this.findRowById(id);

    if (!node) {
      return;
    }

    if (this.gridApiReady()) {
      this.agGrid.api.updateRowData({ remove: [node] });
    }
  }

  nullCoalesceRowData(rowData) {
    if (rowData.length > 0) {
      const keys = Object.keys(rowData[0]);
      for (const row of rowData) {
        for (const key of keys) {
          row[key] = row[key] ?? '';
        }
      }
    }
    return rowData;
  }

  updateRowById(data, id) {
    const node = this.findRowById(id);
    if (!node || !node.data) {
      return;
    }

    node.data = { ...node.data, ...data };

    if (this.gridApiReady()) {
      this.agGrid.api.refreshCells({ rowNodes: [node] });
    }
  }

  getRowHeight() {
    if (this.props.rowHeight) {
      return this.props.rowHeight;
    }

    if (this.state.theme === 'comfort') {
      return 48;
    }

    return 29;
  }

  getHeaderHeight() {
    if (this.state.theme === 'comfort') {
      return 48;
    }

    return 29;
  }

  gridApiReady() {
    if (!this.agGrid || !this.agGrid.api) {
      return false;
    }

    return true;
  }

  render() {
    const conditionalProps = {};
    if (!this.gridOptions.datasource) {
      conditionalProps.rowData = this.nullCoalesceRowData(this.props.rowData);
      this.gridOptions.onColumnRowGroupChanged = ({ columns, columnApi }) => {
        columnApi.applyColumnState({
          state: [{ colId: 'toolbar', pinned: !!columns.length ? null : 'left' }],
        });
      };
    }

    const sideBar = {
      toolPanels: [
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
        },
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressPivotMode: true,
            suppressValues: true,
          },
        },
      ],
    };

    return (
      <div id="list-container" className="content-container">
        {this.props.showToolbar && (
          <ListViewToolbar
            ref={this.listViewToolbar}
            canAdd={this.props.canAdd}
            canExport={this.props.canExport}
            customOptions={this.props.customOptions}
            layout={this.state.layout}
            loading={this.props.loading}
            handleAddNew={this.handleAddNew}
            handleClearFilters={this.handleClearFilters}
            handleExport={this.handleExport}
            handleLoadLayout={this.handleLoadLayout}
            handleRefresh={this.handleRefresh}
            handleRemoveLayout={this.handleRemoveLayout}
            handleSaveLayout={this.handleSaveLayout}
            handleSearch={this.handleSearch}
            handleThemeChange={this.handleThemeChange}
            handleFilter={this.props.handleFilter}
            hasFilter={this.state.hasFilter}
            handleToggleVerticalHeaders={this.handleToggleVerticalHeaders}
            terms={this.state.terms}
            theme={this.state.theme}
            onClear={() => {
              this.handleSearch('');
            }}
          />
        )}

        {!!this.props.filterLayout && (
          <FilterBar filters={this.props.filterLayout} handleFilterClear={this.props.handleFilterClear} />
        )}

        <div
          id="grid-wrapper"
          className={classNames('ag-theme-balham', this.state.theme, {
            'ag-vertical-headers': this.state.verticalHeaders,
          })}
        >
          <AgGridReact
            key={this.state.key}
            ref={el => {
              this.agGrid = el;
            }}
            gridOptions={{ ...this.gridOptions, ...this.getServerSideOptions() }}
            quickFilterText={this.props.serverSide ? '' : this.state.terms}
            onGridReady={this.onGridReady}
            onViewportChanged={this.onViewportChanged}
            onFilterChanged={this.onFilterChanged}
            onModelUpdated={this.onModelUpdated}
            getMainMenuItems={this.getMainMenuItems}
            columnDefs={this.getColumnDefs()}
            getContextMenuItems={this.getContextMenuItems}
            modules={AllModules}
            postSortRows={this.postSortRows}
            headerHeight={this.state.verticalHeaders ? '150' : this.getHeaderHeight() + 5}
            sideBar={sideBar}
            suppressPropertyNamesCheck
            rowGroupPanelShow={this.props.serverSide ? 'never' : 'always'}
            rowBuffer={10}
            rowHeight={this.getRowHeight()}
            onCellValueChanged={this.props.onCellValueChanged}
            {...conditionalProps}
          />
        </div>
      </div>
    );
  }
}

ListView.propTypes = {
  attachments: PropTypes.object,
  canAdd: PropTypes.bool,
  canCopy: PropTypes.bool,
  canDelete: PropTypes.bool,
  canEdit: PropTypes.bool,
  canExport: PropTypes.bool,
  canRead: PropTypes.bool,
  canUpload: PropTypes.bool,
  canViewAttachments: PropTypes.bool,
  columnDefs: PropTypes.arrayOf(PropTypes.object),
  count: PropTypes.number,
  customOptions: PropTypes.array,
  getData: PropTypes.func,
  gridOptions: PropTypes.object,
  handleFilter: PropTypes.func,
  handleFilterClear: PropTypes.func,
  handleRefresh: PropTypes.func,
  handleRemoveItem: PropTypes.func,
  filterLayout: PropTypes.arrayOf(PropTypes.object),
  idFieldName: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  initialQuery: PropTypes.string,
  itemName: PropTypes.string,
  layout: PropTypes.object,
  loading: PropTypes.bool,
  namespace: PropTypes.string,
  refreshTime: PropTypes.number,
  rowData: PropTypes.array,
  search: PropTypes.func,
  serverSide: PropTypes.bool,
  showRowToolbar: PropTypes.bool,
  showToolbar: PropTypes.bool,
  verticalHeaders: PropTypes.bool,
};

ListView.defaultProps = {
  attachments: {},
  canAdd: true,
  canCopy: true,
  canDelete: true,
  canEdit: true,
  canExport: true,
  canRead: false,
  canUpload: false,
  canViewAttachments: false,
  columnDefs: [],
  customOptions: [],
  filterLayout: null,
  getData: () => {},
  gridOptions: null,
  handleRefresh: () => Promise.resolve(),
  idFieldName: '',
  initialQuery: '',
  loading: false,
  search: () => {},
  serverSide: false,
  showRowToolbar: true,
  showToolbar: true,
  verticalHeaders: false,
};

export default ListView;
